/**
 * PostHog experiment / feature-flag keys.
 *
 * One source of truth for flag names so frontend, backend, and PostHog UI all
 * reference the same string. When adding a new experiment, document the flag
 * in `docs/experiments/<flag-key>.md`.
 */
export const EXPERIMENT_FLAGS = {
  /**
   * onboarding-text: 3-arm (a/b/c) copy test on the WelcomeScreen "you will need
   * a device" line. Server-decided via the OptiGrowth decider (NOT PostHog) and
   * consumed through `useExperimentVariant`. Arms: `a` (control, current copy) |
   * `b` | `c`. See `docs/experiments/onboarding-text.md`.
   */
  ONBOARDING_TEXT: 'onboarding-text',

  /**
   * account-deletion-text: 2-arm (e/f) copy test on the Company Settings →
   * Delete Account section ("Permanently remove your account" line).
   * Server-decided via the OptiGrowth decider (NOT PostHog) and consumed
   * through `useExperimentVariant`. Arms: `e` (control, current copy) | `f`
   * (same copy with a trailing period). Renamed from `testing2` pre-launch
   * (2026-07-06). See `docs/experiments/account-deletion-text.md`.
   */
  ACCOUNT_DELETION_TEXT: 'account-deletion-text',

  /**
   * welcome-screen: 2-arm A/B test on the Screens-page welcome screen. Consumed
   * through `useWelcomeScreenVariant`, which is PostHog-driven + backend-sticky:
   * it reads the PostHog `welcome-screen` flag, then persists the pick write-once
   * to `company.experiments['welcome-screen']` via setExperimentVariant (NOT the
   * OptiGrowth decider).
   *
   * Arm keys: `control` (the current welcome screen) | `b` (test = the new
   * WelcomeScreenTest). Only `b` renders the test arm; `control`/not-enrolled →
   * the current screen. PostHog sandbox flag (334458) mirrors these keys at
   * control 75% / b 25%.
   *
   * QA preview (non-prod): `?ws=a` / `?ws=b` (short) or the generic
   * `?force_variant_welcome-screen=<arm>`. See `docs/experiments/welcome-screen.md`.
   */
  WELCOME_SCREEN: 'welcome-screen',
} as const;

export type ExperimentFlagKey = (typeof EXPERIMENT_FLAGS)[keyof typeof EXPERIMENT_FLAGS];
